import { css, keyframes } from '@emotion/react';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import t from 'react-translate';
import { GoalItemStatus } from 'redux/schemas/models/mentoring-program-goal-items';
import NvDropdown, {
  NvDropdownAlign,
  NvDropdownButtonStyle,
  NvDropdownCustomItem,
} from 'shared/components/inputs/nv-dropdown';
import {
  hexToRgbaString,
  lowTide,
  warning,
} from 'styles/global_defaults/colors';
import { dunes, moss, oasis, sandstone, seafoam } from 'athena/styles/colors';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import NvTooltip from 'shared/components/nv-tooltip';
import { tooltipZIndex } from 'styles/global_defaults/scaffolding';
import { config } from '@config/pendo.config.json';
import StarsConfetti from 'athena/components/stars-confetti';
import { getGoalStatusValues } from '../constants';

type GoalStatusProps = {
  status: GoalItemStatus;
  goalItemId: number;
  isSelected?: boolean;
  isReadOnly?: boolean;
  onClick?: (newStatus: GoalItemStatus) => void;
  className?: string;
  tooltipText?: string;
};

type ChangeStatusOptionProps = GoalStatusProps & {
  isReadOnly: boolean;
};

const commonStyles = css`
  width: 210px;
`;

const scaleAnimation = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
`;

const styles = (status: GoalItemStatus) => css`
  .goal-status {
    border-radius: 6px;
    white-space: nowrap;
    padding: 4px 12px;
    width: min-content;
    ${status === GoalItemStatus.JUST_STARTING
    && css`
      color: ${sandstone};
      background-color: ${hexToRgbaString(dunes, 0.3)};
    `}
    ${status === GoalItemStatus.MAKING_PROGRESS
    && css`
      color: ${oasis};
      background-color: ${hexToRgbaString(lowTide, 0.3)};
    `}
    ${status === GoalItemStatus.SUBSTANTIAL_PROGRESS
    && css`
      color: ${moss};
      background-color: ${hexToRgbaString(seafoam, 0.3)};
    `}
  }
  .selected-status {
    color: ${warning};
  }
  &.scale-status {
    animation: ${scaleAnimation} 3s ease-in-out infinite;
  }
`;

const GoalStatus = ({
  status,
  isSelected,
  isReadOnly,
  onClick,
  className,
  tooltipText,
  goalItemId,
}: GoalStatusProps) => {
  const statusValues = getGoalStatusValues(t);
  const dataQATag = config.pendo.athena.mentorshipProgram.participantHome.goals.list.item
    .editGoalStatus;

  return (
    <ClickableContainer
      disabled={isReadOnly}
      css={styles(status)}
      className={`${className} text-small bold`}
      onClick={() => onClick?.(status)}
      data-qa={dataQATag}
      data-qa-id={`${dataQATag}-${goalItemId}`}
    >
      <NvTooltip
        enabled={!isReadOnly && !!tooltipText}
        placement='top'
        text={tooltipText}
        zIndex={tooltipZIndex}
      >
        <div className='goal-status'>{statusValues[status]}</div>
      </NvTooltip>
      {isSelected && (
        <NvIcon className='selected-status' icon='check' size='small' />
      )}
    </ClickableContainer>
  );
};

const ChangeStatusOption = ({
  isReadOnly,
  status,
  goalItemId,
}: ChangeStatusOptionProps) => {
  const { setValue } = useFormContext();
  const statusValues = getGoalStatusValues(t);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);

  const onChangeStatus = (newStatus: GoalItemStatus) => {
    setValue('status', newStatus);
    setIsDropdownOpen(false);
    setShowAnimation(true);
  };

  const statusTitle = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GOALS.ITEM.STATUS.TITLE();

  const titleItem: NvDropdownCustomItem = {
    type: 'custom',
    customItem: (
      <div css={commonStyles} className='m-3'>
        {statusTitle}
      </div>
    ),
  };

  useEffect(() => {
    if (showAnimation) {
      setTimeout(() => {
        setShowAnimation(false);
      }, 3000);
    }
  }, [showAnimation]);

  const statusItemsOptions: NvDropdownCustomItem[] = Object.entries(
    statusValues,
  ).map(([key, _]) => ({
    type: 'custom',
    customItem: (
      <GoalStatus
        status={key as GoalItemStatus}
        goalItemId={goalItemId}
        isSelected={key === status}
        onClick={onChangeStatus}
        className='m-3 d-flex justify-content-between'
      />
    ),
  }));

  const getCustomTarget = () => {
    const goalStatus = (
      <GoalStatus
        status={status}
        goalItemId={goalItemId}
        isReadOnly={isReadOnly}
        tooltipText={statusTitle}
        className={`d-flex justify-content-end mb-1 ${showAnimation ? 'scale-status' : ''}`}
      />
    );
    if (showAnimation) {
      return (
        <StarsConfetti
          showCenter={showAnimation}
          starsAmount={{ min: 10, max: 15 }}
        >
          {goalStatus}
        </StarsConfetti>
      );
    }
    return goalStatus;
  };

  return (
    <NvDropdown
      disabled={isReadOnly}
      items={[titleItem, ...statusItemsOptions]}
      buttonStyle={NvDropdownButtonStyle.CUSTOM}
      customTarget={getCustomTarget}
      align={NvDropdownAlign.RIGHT}
      title={statusValues[status]}
      onToggle={() => {
        setIsDropdownOpen((prevState) => !prevState);
      }}
      show={isDropdownOpen}
      ariaExpanded={isDropdownOpen}
      altLabel={status}
      buttonClass='justify-content-right'
      ariaRole='menu'
    />
  );
};

export default ChangeStatusOption;
